import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

const createManualLine = () => ({
  id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  inventoryItemId: '',
  title: '',
  category: '',
  qty: 1,
  price: '',
});

const SCAN_DIAG_ENABLED = false;
const DIAG_PREFIX = '[SoldScanDiag]';
const SCAN_CONSOLE_DEBUG_ENABLED = false;
const logScanDiag = (event, payload = {}) => {
  if (!SCAN_DIAG_ENABLED) return;
  try {
    console.log(DIAG_PREFIX, { event, ...payload });
  } catch {
  }
};

const debugScanConsole = (...args) => {
  if (!SCAN_CONSOLE_DEBUG_ENABLED) return;
  try {
    console.log(...args);
  } catch {
  }
};

const safeErrorString = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'unknown';
  return raw.length > 220 ? `${raw.slice(0, 220)}...` : raw;
};

const toPositiveIntegerOrNull = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

const normalizeSalesTransactionMeta = (value) => {
  const row = Array.isArray(value) ? value[0] : value;
  if (row && typeof row === 'object') {
    return {
      id: String(row.id || row.sales_trans_id || '').trim(),
      saleTransNumber: toPositiveIntegerOrNull(row.sale_trans_number ?? row.saleTransNumber),
      stripePaymentUrl: String(row.stripe_payment_url || row.stripePaymentUrl || row.url || '').trim(),
      stripeSessionId: String(row.stripe_session_id || row.stripeSessionId || row.session_id || '').trim(),
    };
  }
  return {
    id: String(value || '').trim(),
    saleTransNumber: null,
    stripePaymentUrl: '',
    stripeSessionId: '',
  };
};

const QR_SCANNER_WORKER_PATH = `${process.env.PUBLIC_URL || ''}/qr-scanner-worker.min.js`;
const QR_DUPLICATE_COOLDOWN_MS = 900;
const QR_SCAN_REGION_RATIO = 0.85;
const QR_SCAN_DOWNSCALED_SIZE = 900;
const QR_AUTO_SWITCH_ENABLED = false;
const QR_AUTO_SWITCH_MAX = 1;
const QR_AUTO_SWITCH_TRIGGER_ATTEMPTS = 40;
const QR_REFOCUS_TRIGGER_ATTEMPTS = 80;
const QR_IOS_NO_HIT_SWITCH_ENABLED = false;
const QR_IOS_NO_HIT_SWITCH_TRIGGER_ATTEMPTS = 80;
const QR_PAUSE_AFTER_SUCCESS = true;
const QR_NO_HIT_TIMEOUT_MS = 30000;
const INCOMPLETE_MANUAL_LINE_ERROR = 'Select category and price or remove item.';
const DUPLICATE_SCANNED_ITEM_ERROR = 'Item already in checkout. Remove it first to scan again.';
const MANUAL_ITEM_DEFAULT_TITLE = 'manually enter sold item';

QrScanner.WORKER_PATH = QR_SCANNER_WORKER_PATH;

const parseQrToken = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/[0-9a-fA-F-]{36}/);
  if (match) return match[0];
  return text;
};

const extractDecodedText = (decoded) => {
  if (typeof decoded === 'string') return decoded;
  if (decoded && typeof decoded.data === 'string') return decoded.data;
  if (decoded && typeof decoded.rawValue === 'string') return decoded.rawValue;
  return '';
};

const rankBackCamera = (camera) => {
  const label = String(camera?.label || '').toLowerCase();
  if (!label) return 0;
  // iPhones tend to be most stable for printed QR with dual-wide/standard rear lenses.
  if (isIOSWebkit()) {
    if (/back dual wide/.test(label)) return 190;
    if (/back camera/.test(label)) return 180;
    if (/back dual(?!\s*wide)/.test(label)) return 170;
    if (/back triple/.test(label)) return 160;
    if (/back telephoto/.test(label)) return 90;
    if (/back ultra wide/.test(label)) return 80;
  }
  // Default priority for non-iOS.
  if (/back triple/.test(label)) return 140;
  if (/back camera/.test(label)) return 130;
  if (/back dual wide/.test(label)) return 120;
  if (/back dual(?!\s*wide)/.test(label)) return 110;
  if (/back ultra wide/.test(label)) return 90;
  if (/back telephoto/.test(label)) return 60;
  if (/rear|back|environment/.test(label)) return 60;
  return 10;
};

const isIOSWebkit = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  return /iPhone|iPad|iPod/i.test(ua);
};

const buildRearCameraCandidates = (cameras) => {
  if (!Array.isArray(cameras) || !cameras.length) return null;
  const ranked = cameras
    .map((camera) => ({ camera, score: rankBackCamera(camera) }))
    .sort((a, b) => b.score - a.score)
    .map((row) => row.camera);
  return ranked.length ? ranked : cameras;
};

const pickPreferredBackCamera = (cameras) => {
  const candidates = buildRearCameraCandidates(cameras);
  if (!candidates || !candidates.length) return null;
  const dualWide = candidates.find((camera) =>
    /back dual wide/.test(String(camera?.label || '').toLowerCase())
  );
  if (dualWide) return dualWide;
  return candidates[0] || null;
};

const formatCameraStartError = (err) => {
  const name = String(err?.name || '');
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Camera access is required. Please enable it in your browser settings.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No camera was found on this device.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Camera is busy in another app or browser tab.';
  }
  if (name === 'OverconstrainedError') {
    return 'Requested camera settings are not supported on this device.';
  }
  return safeErrorString(err?.message || err || 'Unable to start camera.');
};

const calculateHighFidelityScanRegion = (video) => {
  const videoWidth = Number(video?.videoWidth || 0);
  const videoHeight = Number(video?.videoHeight || 0);
  if (!videoWidth || !videoHeight) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      downScaledWidth: QR_SCAN_DOWNSCALED_SIZE,
      downScaledHeight: QR_SCAN_DOWNSCALED_SIZE,
    };
  }

  const side = Math.max(
    240,
    Math.round(Math.min(videoWidth, videoHeight) * QR_SCAN_REGION_RATIO)
  );
  // Never upscale the decode region; upscaling softens printed QR edges.
  const downScaledSide = Math.max(240, Math.min(QR_SCAN_DOWNSCALED_SIZE, side));
  const x = Math.max(0, Math.round((videoWidth - side) / 2));
  const y = Math.max(0, Math.round((videoHeight - side) / 2));
  return {
    x,
    y,
    width: side,
    height: side,
    downScaledWidth: downScaledSide,
    downScaledHeight: downScaledSide,
  };
};

const getActiveVideoTrack = (videoEl) => {
  const stream = videoEl?.srcObject;
  if (!stream || typeof stream.getVideoTracks !== 'function') return null;
  const [track] = stream.getVideoTracks();
  return track || null;
};

const summarizeTrackSettings = (settings) => ({
  deviceId: settings?.deviceId || null,
  width: settings?.width || null,
  height: settings?.height || null,
  frameRate: settings?.frameRate || null,
  focusMode: settings?.focusMode || null,
  focusDistance: settings?.focusDistance ?? null,
  zoom: settings?.zoom || null,
});

const summarizeTrackCapabilities = (capabilities) => ({
  width: capabilities?.width || null,
  height: capabilities?.height || null,
  frameRate: capabilities?.frameRate || null,
  zoom: capabilities?.zoom || null,
  focusDistance: capabilities?.focusDistance || null,
  focusMode: Array.isArray(capabilities?.focusMode) ? capabilities.focusMode : [],
  torch: capabilities?.torch || false,
});

const isNoQrDecodeMessage = (message) => {
  const text = String(message || '').toLowerCase();
  return text.includes('no qr code found') || text.includes('not found');
};

const normalizePercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  if (num < 0) return 0;
  if (num > 100) return 100;
  return num;
};

const roundMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.round((number + Number.EPSILON) * 100) / 100;
};

const calculateCheckoutUnitPrice = (listPrice, discountPct) => {
  if (listPrice === null || listPrice === undefined) return null;
  const price = Number(listPrice);
  if (!Number.isFinite(price)) return null;
  const discountedPrice = Number((price * (1 - normalizePercent(discountPct) / 100)).toFixed(2));
  if (discountedPrice > 0) return discountedPrice;
  return price > 0 ? price : discountedPrice;
};

const getCapacitorRuntime = () => {
  if (typeof window === 'undefined') return null;
  const runtime = window.Capacitor || null;
  if (!runtime) return null;
  const platform = typeof runtime.getPlatform === 'function'
    ? String(runtime.getPlatform() || 'web')
    : 'web';
  const isNative = typeof runtime.isNativePlatform === 'function'
    ? Boolean(runtime.isNativePlatform())
    : platform !== 'web';
  if (!isNative) return null;
  return runtime;
};

const getNativeBarcodeScannerPlugin = () => {
  const runtime = getCapacitorRuntime();
  if (!runtime) return null;
  const plugins = runtime.Plugins || {};
  return (
    plugins.BarcodeScanner ||
    plugins.CapacitorBarcodeScanner ||
    plugins.MLKITBarcodeScanner ||
    plugins.MLKitBarcodeScanner ||
    null
  );
};

const isNativeScanCancelledResult = (result) => {
  if (!result || typeof result !== 'object') return false;
  if (result.cancelled === true || result.canceled === true || result.didCancel === true) return true;
  if (result.hasContent === false && !result.content) return true;
  if (result.hasData === false && !result.data) return true;
  if (result.status && String(result.status).toLowerCase() === 'cancelled') return true;
  return false;
};

const isNativeScanCancelledError = (err) => {
  const name = String(err?.name || '').toLowerCase();
  const message = String(err?.message || '').toLowerCase();
  const combined = `${name} ${message}`.trim();
  return (
    combined.includes('cancel') ||
    combined.includes('user cancelled') ||
    combined.includes('user canceled') ||
    combined.includes('abort')
  );
};

const extractNativeScanToken = (rawResult) => {
  const values = [];
  const pushValue = (value) => {
    if (typeof value === 'string' && value.trim()) {
      values.push(value);
    }
  };

  if (typeof rawResult === 'string') {
    pushValue(rawResult);
  } else if (rawResult && typeof rawResult === 'object') {
    pushValue(rawResult.content);
    pushValue(rawResult.data);
    pushValue(rawResult.rawValue);
    pushValue(rawResult.value);
    pushValue(rawResult.text);
    pushValue(rawResult.code);
    pushValue(rawResult.qr);
    pushValue(rawResult.result);

    const barcodeSets = [];
    if (Array.isArray(rawResult.barcodes)) barcodeSets.push(rawResult.barcodes);
    if (Array.isArray(rawResult.codes)) barcodeSets.push(rawResult.codes);
    for (const set of barcodeSets) {
      for (const row of set) {
        pushValue(row?.rawValue);
        pushValue(row?.displayValue);
        pushValue(row?.value);
        pushValue(row?.text);
        pushValue(row?.content);
        pushValue(row?.data);
      }
    }
  }

  for (const value of values) {
    const token = parseQrToken(value);
    if (token) return token;
  }
  return '';
};

const tuneVideoTrackForScan = async (videoEl, sessionId, reason) => {
  const track = getActiveVideoTrack(videoEl);
  if (!track || typeof track.applyConstraints !== 'function') return;

  const capabilities = typeof track.getCapabilities === 'function'
    ? track.getCapabilities() || {}
    : {};
  logScanDiag('track_capabilities', {
    sessionId,
    reason,
    hasZoom: Boolean(capabilities.zoom),
    zoomMax: capabilities.zoom?.max || null,
    focusModes: Array.isArray(capabilities.focusMode) ? capabilities.focusMode : [],
  });

  const attempts = [];
  const isIos = isIOSWebkit();
  const shouldApplyResolutionHint = !isIos;
  if (shouldApplyResolutionHint && (reason === 'auto_camera_switch' || reason === 'post_start')) {
    attempts.push({
      label: 'res_1280x720',
      constraints: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
      },
    });
  }

  const focusModes = Array.isArray(capabilities.focusMode) ? capabilities.focusMode : [];
  if (focusModes.includes('continuous')) {
    attempts.push({
      label: 'focus_continuous',
      constraints: { advanced: [{ focusMode: 'continuous' }] },
    });
  }

  if (reason === 'post_hit_refocus' || reason === 'no_hit_refocus') {
    if (capabilities.zoom) {
      attempts.push({
        label: 'zoom_reset',
        constraints: { advanced: [{ zoom: 1 }] },
      });
    }
  }

  for (const attempt of attempts) {
    try {
      await track.applyConstraints(attempt.constraints);
      const settings = typeof track.getSettings === 'function' ? track.getSettings() : {};
      logScanDiag('track_constraint_applied', {
        sessionId,
        reason,
        label: attempt.label,
        settings: summarizeTrackSettings(settings),
      });
    } catch (err) {
      logScanDiag('track_constraint_failed', {
        sessionId,
        reason,
        label: attempt.label,
        errorName: String(err?.name || ''),
        errorMessage: safeErrorString(err?.message || err),
      });
    }
  }
};

export default function useSoldScan({
  apiUrl,
  clientId,
  fetchWrapper,
  selectedSaleId,
  saleDiscPct = 0,
  setSelectedSaleId,
  setSelectedSectionId,
  fetchInventory,
  resolveManualSection,
  toNumberOrNull,
  toIntOr,
}) {
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanItems, setScanItems] = useState([]);
  const [manualLines, setManualLines] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanErrorItemId, setScanErrorItemId] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scanInProgress, setScanInProgress] = useState(false);
  const scannerRef = useRef(null);
  const scannerVideoElRef = useRef(null);
  const scanGuardRef = useRef(false);
  const lastTokenScanAtRef = useRef(new Map());
  const handleTokenScanRef = useRef(null);
  const decodeAttemptsRef = useRef(0);
  const decodeHitsRef = useRef(0);
  const decodeErrorSampleRef = useRef('');
  const decodeNoQrCountRef = useRef(0);
  const decodeOtherErrorCountRef = useRef(0);
  const decodeAttemptsSinceHitRef = useRef(0);
  const scanSessionDiagRef = useRef(null);
  const scannerSessionRef = useRef(0);
  const modalLockedCameraIdRef = useRef('');
  const modalLockedCameraLabelRef = useRef('');
  const lastQrRef = useRef('');
  const lastQrAuthedRef = useRef(false);

  const resetScanModal = useCallback(() => {
    setScanItems([]);
    setManualLines([]);
    setManualOpen(false);
    setScanError('');
    setScanErrorItemId('');
    setShowScanner(false);
    setScanModalOpen(false);
    modalLockedCameraIdRef.current = '';
    modalLockedCameraLabelRef.current = '';
  }, []);

  const emitSessionSummary = useCallback((reason, extra = {}) => {
    const session = scanSessionDiagRef.current;
    if (!session || session.ended) return;
    session.ended = true;
    const elapsedMs = Date.now() - session.startedAt;
    logScanDiag('session_summary', {
      sessionId: session.sessionId,
      reason,
      elapsedMs,
      decodeAttempts: decodeAttemptsRef.current,
      decodeHits: decodeHitsRef.current,
      decodeNoQr: decodeNoQrCountRef.current,
      decodeOtherErrors: decodeOtherErrorCountRef.current,
      autoSwitches: session.autoSwitches || 0,
      iosNoHitFallbackSwitches: session.iosNoHitFallbackSwitches || 0,
      activeCameraIndex: session.activeCameraIndex ?? null,
      candidateCount: session.candidateCount ?? null,
      activeCameraId: session.activeCameraId || null,
      activeCameraLabel: session.activeCameraLabel || '',
      frameCallbacks: session.frameCallbacks || 0,
      frameLogEmits: session.frameLogEmits || 0,
      videoEventCounts: session.videoEventCounts || {},
      lastDecodeSample: decodeErrorSampleRef.current || '',
      ...extra,
    });
  }, []);

  const stopScanner = useCallback(async (options = {}) => {
    const shouldInvalidate = options.invalidateSession !== false;
    const shouldHideScanner = options.hideScanner !== false;
    const reason = options.reason || 'stop_scanner';
    if (shouldInvalidate) {
      scannerSessionRef.current += 1;
    }

    const scanner = scannerRef.current;
    scannerRef.current = null;

    const videoEl = scannerVideoElRef.current;
    scannerVideoElRef.current = null;

    if (scanner) {
      try {
        await scanner.stop();
      } catch {
      }
      try {
        scanner.destroy();
      } catch {
      }
    }

    // If a native scanner plugin is active, close it as well.
    const nativePlugin = getNativeBarcodeScannerPlugin();
    if (nativePlugin) {
      if (typeof nativePlugin.stopScan === 'function') {
        try {
          await nativePlugin.stopScan();
        } catch {
        }
      }
      if (typeof nativePlugin.stop === 'function') {
        try {
          await nativePlugin.stop();
        } catch {
        }
      }
      if (typeof nativePlugin.showBackground === 'function') {
        try {
          await nativePlugin.showBackground();
        } catch {
        }
      }
    }

    if (videoEl) {
      const activeTrack = getActiveVideoTrack(videoEl);
      const finalSettings = activeTrack?.getSettings?.() || {};
      const finalCapabilities = activeTrack?.getCapabilities?.() || {};
      if (activeTrack) {
        logScanDiag('stop_scanner_track_snapshot', {
          reason,
          settings: summarizeTrackSettings(finalSettings),
          capabilities: summarizeTrackCapabilities(finalCapabilities),
        });
      }
      try {
        const stream = videoEl.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch {
            }
          });
        }
      } catch {
      }
      try {
        videoEl.srcObject = null;
      } catch {
      }
      try {
        if (videoEl.parentElement) {
          videoEl.parentElement.removeChild(videoEl);
        }
      } catch {
      }
    }

    scanGuardRef.current = false;
    lastTokenScanAtRef.current.clear();
    emitSessionSummary(reason, {
      shouldInvalidate,
      shouldHideScanner,
    });
    scanSessionDiagRef.current = null;
    decodeAttemptsRef.current = 0;
    decodeHitsRef.current = 0;
    decodeErrorSampleRef.current = '';
    decodeNoQrCountRef.current = 0;
    decodeOtherErrorCountRef.current = 0;
    decodeAttemptsSinceHitRef.current = 0;
    if (shouldHideScanner) {
      setShowScanner(false);
    }
    setScanInProgress(false);
  }, [emitSessionSummary]);

  const resolveQrToken = useCallback(async (token) => {
    const startedAt = Date.now();
    logScanDiag('resolve_qr_start', {
      token,
      tokenSuffix: String(token || '').slice(-8),
    });
    debugScanConsole('[SoldScan] resolveQrToken start', { token });
    const resp = await fetchWrapper(
      `${apiUrl}/supabase/rpc/select_inventory_item_by_qr_token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_client_id: clientId,
          p_qr_token: token,
        }),
      }
    );
    debugScanConsole('[SoldScan] resolveQrToken response', { status: resp.status });
    logScanDiag('resolve_qr_response', {
      tokenSuffix: String(token || '').slice(-8),
      status: resp.status,
      ok: resp.ok,
      elapsedMs: Date.now() - startedAt,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error?.message || 'QR lookup failed');
    }
    const data = await resp.json();
    debugScanConsole('[SoldScan] resolveQrToken data', { hasRow: Array.isArray(data) ? data.length > 0 : Boolean(data) });
    logScanDiag('resolve_qr_data', {
      tokenSuffix: String(token || '').slice(-8),
      hasRow: Array.isArray(data) ? data.length > 0 : Boolean(data),
      rowCount: Array.isArray(data) ? data.length : data ? 1 : 0,
      elapsedMs: Date.now() - startedAt,
    });
    if (Array.isArray(data)) return data[0] || null;
    return data || null;
  }, [apiUrl, clientId, fetchWrapper]);

  const resolveActiveItemForToken = useCallback(async (token, saleId) => {
    if (!token || !saleId) return null;
    try {
      const resp = await fetchWrapper(
        `${apiUrl}/supabase/rpc/select_inventory_items_by_statuses`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_sale_id: saleId,
            p_client_id: clientId,
            p_section_id: null,
            p_statuses: ['active', 'on hold'],
          }),
        }
      );
      if (!resp.ok) return null;
      const data = await resp.json();
      if (!Array.isArray(data)) return null;
      const match = data.find((row) => {
        const rowToken = String(
          row?.qr_token || row?.qrToken || row?.token || row?.qr || ''
        ).trim();
        return rowToken === token;
      });
      if (match) return match;

      // Fallback: try to identify the active row even when token field is not projected by RPC.
      // We only auto-pick when there is a single high-confidence match to avoid wrong sales.
      return null;
    } catch (err) {
      console.warn('[SoldScan] active-token fallback lookup failed:', err?.message || err);
      return null;
    }
  }, [apiUrl, clientId, fetchWrapper]);

  const findLikelyActiveMatch = useCallback((soldItem, activeRows, token) => {
    if (!soldItem || !Array.isArray(activeRows) || activeRows.length === 0) return null;
    const normalize = (v) => String(v || '').trim().toLowerCase();
    const soldTitle = normalize(soldItem.title);
    const soldSection = normalize(soldItem.section_id || soldItem.section_name);
    const soldFilename = normalize(soldItem.filename || soldItem.file_name);
    const soldPrice = toNumberOrNull(soldItem.per_item_cost_estimate);
    const soldId = soldItem.id || '';
    const soldOrigin =
      soldItem.original_item_id ||
      soldItem.parent_item_id ||
      soldItem.inventory_item_id ||
      soldItem.source_item_id ||
      '';
    const scored = activeRows.map((row) => {
      let score = 0;
      const rowToken = String(
        row?.qr_token || row?.qrToken || row?.token || row?.qr || ''
      ).trim();
      if (token && rowToken && rowToken === token) score += 100;

      const rowId = row.id || '';
      const rowOrigin =
        row.original_item_id ||
        row.parent_item_id ||
        row.inventory_item_id ||
        row.source_item_id ||
        '';
      if (soldOrigin && rowId && soldOrigin === rowId) score += 80;
      if (soldId && rowOrigin && soldId === rowOrigin) score += 80;
      if (soldOrigin && rowOrigin && soldOrigin === rowOrigin) score += 50;

      if (soldTitle && normalize(row.title) === soldTitle) score += 10;
      if (soldSection && normalize(row.section_id || row.section_name) === soldSection) score += 8;
      if (soldFilename && normalize(row.filename || row.file_name) === soldFilename) score += 8;

      const rowPrice = toNumberOrNull(row.per_item_cost_estimate);
      if (soldPrice !== null && rowPrice !== null && Math.abs(soldPrice - rowPrice) < 0.001) score += 6;

      const rowQty = toIntOr(row.quantity_estimate, 1);
      if (rowQty > 0) score += 2;
      return { row, score };
    });
    scored.sort((a, b) => b.score - a.score);
    const best = scored[0];
    const second = scored[1];
    if (!best || best.score < 20) return null;
    if (second && second.score === best.score) return null;
    return best.row || null;
  }, [toIntOr, toNumberOrNull]);

  const fetchActiveCandidates = useCallback(async (saleId) => {
    if (!saleId) return [];
    const resp = await fetchWrapper(
      `${apiUrl}/supabase/rpc/select_inventory_items_by_statuses`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_sale_id: saleId,
          p_client_id: clientId,
          p_section_id: null,
          p_statuses: ['active', 'on hold'],
        }),
      }
    );
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data) ? data : [];
  }, [apiUrl, clientId, fetchWrapper]);

  const addScannedItem = useCallback((item, token) => {
    const qtyAvailable = toIntOr(item.quantity_estimate, 1);
    const saleId = item.sale_id || selectedSaleId;
    const listPrice = toNumberOrNull(item.per_item_cost_estimate);
    const discountPct = normalizePercent(saleDiscPct);
    const discountedEach = calculateCheckoutUnitPrice(listPrice, discountPct);
    const soldPriceValue = discountedEach ?? listPrice;
    const baseItem = {
      id: item.id,
      token,
      saleId,
      title: item.title || 'N/A',
      category: String(item.category || '').trim(),
      status: item.status || 'active',
      qtyAvailable,
      qtySold: 1,
      listPrice,
      soldPrice:
        soldPriceValue === null || soldPriceValue === undefined
          ? ''
          : Number(soldPriceValue).toFixed(2),
      discountPct,
    };

    setScanItems((prev) => {
      const existing = prev.find((row) => row.id === baseItem.id);
      if (!existing) {
        return [...prev, baseItem];
      }
      const nextQty = Math.min(existing.qtySold + 1, existing.qtyAvailable);
      return prev.map((row) =>
        row.id === baseItem.id ? { ...row, qtySold: nextQty } : row
      );
    });
  }, [saleDiscPct, selectedSaleId, toIntOr, toNumberOrNull]);

  const buildScanItemFromInventoryRow = useCallback((item, token = '') => {
    const qtyAvailable = toIntOr(item.quantity_estimate, 1);
    const saleId = item.sale_id || selectedSaleId;
    const listPrice = toNumberOrNull(item.per_item_cost_estimate);
    const discountPct = normalizePercent(saleDiscPct);
    const discountedEach = calculateCheckoutUnitPrice(listPrice, discountPct);
    const soldPriceValue = discountedEach ?? listPrice;
    const baseItem = {
      id: item.id,
      token,
      saleId,
      title: item.title || 'N/A',
      category: String(item.category || '').trim(),
      status: item.status || 'active',
      qtyAvailable,
      qtySold: 1,
      listPrice,
      soldPrice:
        soldPriceValue === null || soldPriceValue === undefined
          ? ''
          : Number(soldPriceValue).toFixed(2),
      discountPct,
    };
    return baseItem;
  }, [saleDiscPct, selectedSaleId, toIntOr, toNumberOrNull]);

  const loadSelectedItemsIntoModal = useCallback(async (rows = []) => {
    const selectedRows = Array.isArray(rows) ? rows : [];
    if (!selectedRows.length) {
      return { added: 0, skippedSold: 0, skippedExisting: 0 };
    }

    await stopScanner({ reason: 'selected_items_launch_modal' });

    const seen = new Set();
    const prepared = [];
    let skippedSold = 0;
    for (const row of selectedRows) {
      if (!row?.id || seen.has(row.id)) continue;
      seen.add(row.id);
      const status = String(row.status || '').trim().toLowerCase();
      if (status === 'sold') {
        skippedSold += 1;
        continue;
      }
      prepared.push(buildScanItemFromInventoryRow(row));
    }

    if (!prepared.length) {
      setScanError('No eligible selected items to sell.');
      return { added: 0, skippedSold, skippedExisting: 0 };
    }

    const existingIds = new Set(scanItems.map((item) => item.id));
    const nextScanItems = [...scanItems];
    let added = 0;
    let skippedExisting = 0;
    for (const item of prepared) {
      if (existingIds.has(item.id)) {
        skippedExisting += 1;
        continue;
      }
      nextScanItems.push(item);
      existingIds.add(item.id);
      added += 1;
    }
    setScanItems(nextScanItems);

    setScanModalOpen(true);
    setShowScanner(false);
    setScanInProgress(false);
    setScanError('');
    logScanDiag('selected_items_loaded', {
      selectedCount: selectedRows.length,
      preparedCount: prepared.length,
      added,
      skippedSold,
      skippedExisting,
    });
    return { added, skippedSold, skippedExisting };
  }, [buildScanItemFromInventoryRow, scanItems, stopScanner]);

  const handleTokenScan = useCallback(async (token) => {
    const startedAt = Date.now();
    debugScanConsole('[SoldScan] handleTokenScan', { token, scanModalOpen });
    logScanDiag('handle_token_scan_start', {
      token,
      tokenSuffix: String(token || '').slice(-8),
      scanModalOpen,
      currentScannedCount: scanItems.length,
    });
    if (!token) {
      setScanError('Invalid QR code. Try again.');
      logScanDiag('handle_token_scan_invalid', {
        elapsedMs: Date.now() - startedAt,
      });
      return;
    }
    if (!scanModalOpen) {
      setScanModalOpen(true);
    }
    setScanError('');
    try {
      const lookedUpItem = await resolveQrToken(token);
      if (!lookedUpItem) {
        setScanError('Item not found for this QR code.');
        logScanDiag('handle_token_scan_not_found', {
          tokenSuffix: String(token || '').slice(-8),
          elapsedMs: Date.now() - startedAt,
        });
        return;
      }
      let item = lookedUpItem;
      const lookedUpStatus = String(lookedUpItem.status || '').toLowerCase();
      const lookedUpSoldPrice = toNumberOrNull(lookedUpItem.sold_price);
      if (lookedUpStatus === 'sold' && lookedUpSoldPrice && lookedUpSoldPrice > 0) {
        const saleIdForFallback = lookedUpItem.sale_id || selectedSaleId;
        const fallbackStartedAt = Date.now();
        let activeCandidate = await resolveActiveItemForToken(token, saleIdForFallback);
        if (!activeCandidate && saleIdForFallback) {
          const activeRows = await fetchActiveCandidates(saleIdForFallback);
          activeCandidate = findLikelyActiveMatch(lookedUpItem, activeRows, token);
        }
        if (activeCandidate) {
          debugScanConsole('[SoldScan] handleTokenScan using active fallback item', {
            soldItemId: lookedUpItem.id,
            activeItemId: activeCandidate.id,
          });
          item = activeCandidate;
          logScanDiag('handle_token_scan_fallback_active_match', {
            soldItemId: lookedUpItem.id,
            activeItemId: activeCandidate.id,
            saleId: saleIdForFallback,
            elapsedMs: Date.now() - fallbackStartedAt,
          });
        } else {
          debugScanConsole('[SoldScan] no active fallback match found for sold QR result', {
            soldItemId: lookedUpItem.id,
            saleId: saleIdForFallback,
            lookedUpKeys: Object.keys(lookedUpItem || {}),
          });
          logScanDiag('handle_token_scan_fallback_no_match', {
            soldItemId: lookedUpItem.id,
            saleId: saleIdForFallback,
            lookedUpStatus,
            lookedUpKeys: Object.keys(lookedUpItem || {}),
            elapsedMs: Date.now() - fallbackStartedAt,
          });
        }
      }
      debugScanConsole('[SoldScan] handleTokenScan item', { itemId: item.id, saleId: item.sale_id, status: item.status });
      const activeCameraId = scanSessionDiagRef.current?.activeCameraId || '';
      const activeCameraLabel = scanSessionDiagRef.current?.activeCameraLabel || '';
      if (!modalLockedCameraIdRef.current && activeCameraId) {
        modalLockedCameraIdRef.current = activeCameraId;
        modalLockedCameraLabelRef.current = activeCameraLabel || modalLockedCameraLabelRef.current;
        logScanDiag('camera_locked_for_modal', {
          cameraId: modalLockedCameraIdRef.current,
          cameraLabel: modalLockedCameraLabelRef.current || '',
          itemId: item.id,
          tokenSuffix: String(token || '').slice(-8),
          elapsedMs: Date.now() - startedAt,
        });
      }
      const existing = scanItems.find((row) => row.id === item.id);
      const soldPrice = toNumberOrNull(item.sold_price);
      if (String(item.status || '').toLowerCase() === 'sold' && soldPrice && soldPrice > 0) {
        await stopScanner({ invalidateSession: false, reason: 'sold_prompt' });
        logScanDiag('handle_token_scan_paused_for_sold_prompt', {
          itemId: item.id,
          elapsedMs: Date.now() - startedAt,
        });
        const proceed = window.confirm('This item is already marked sold. Add a manual sale for it?');
        if (!proceed) {
          setScanError('Item is already sold. Scan another item or add a manual line.');
          logScanDiag('handle_token_scan_user_cancelled_sold_prompt', {
            itemId: item.id,
            elapsedMs: Date.now() - startedAt,
            modalRemainsOpen: true,
          });
          return;
        }
        setManualOpen(true);
        const saleDiscount = normalizePercent(saleDiscPct);
        const basePrice = toNumberOrNull(item.per_item_cost_estimate);
        const discountedPrice = basePrice === null
          ? ''
          : calculateCheckoutUnitPrice(basePrice, saleDiscount);
        setManualLines((prev) => [
          ...prev,
          {
            id: `manual-sold-${Date.now()}`,
            inventoryItemId: '',
            title: item.title || 'Manual Item',
            category: '',
            qty: 1,
            price: discountedPrice,
          },
        ]);
        return;
      }
      if (existing && existing.qtyAvailable <= 1 && existing.qtySold >= 1) {
        await stopScanner({ invalidateSession: false, reason: 'duplicate_single_qty_item' });
        setScanError(DUPLICATE_SCANNED_ITEM_ERROR);
        logScanDiag('handle_token_scan_duplicate_single_qty_item', {
          itemId: item.id,
          elapsedMs: Date.now() - startedAt,
        });
        return;
      }
      if (existing && existing.qtySold >= existing.qtyAvailable) {
        await stopScanner({ invalidateSession: false, reason: 'qty_prompt' });
        logScanDiag('handle_token_scan_paused_for_qty_prompt', {
          itemId: item.id,
          elapsedMs: Date.now() - startedAt,
        });
        const proceed = window.confirm('Qty exceeds available. Add a manual sale for the extra item?');
        if (!proceed) {
          logScanDiag('handle_token_scan_user_cancelled_qty_prompt', {
            itemId: item.id,
            elapsedMs: Date.now() - startedAt,
          });
          return;
        }
        setManualOpen(true);
        const saleDiscount = normalizePercent(saleDiscPct);
        const basePrice = toNumberOrNull(item.per_item_cost_estimate);
        const discountedPrice = basePrice === null
          ? ''
          : calculateCheckoutUnitPrice(basePrice, saleDiscount);
        setManualLines((prev) => [
          ...prev,
          {
            id: `manual-extra-${Date.now()}`,
            inventoryItemId: '',
            title: item.title || 'Manual Item',
            category: '',
            qty: 1,
            price: discountedPrice,
          },
        ]);
        return;
      }
      if (item.sale_id && item.sale_id !== selectedSaleId) {
        setSelectedSaleId(item.sale_id);
        setSelectedSectionId('all');
        logScanDiag('handle_token_scan_switched_sale', {
          fromSaleId: selectedSaleId,
          toSaleId: item.sale_id,
        });
      }
      addScannedItem(item, token);
      if (QR_PAUSE_AFTER_SUCCESS) {
        await stopScanner({ invalidateSession: false, reason: 'scan_success_pause' });
      }
      logScanDiag('handle_token_scan_success', {
        itemId: item.id,
        status: item.status,
        saleId: item.sale_id || selectedSaleId,
        pausedAfterSuccess: QR_PAUSE_AFTER_SUCCESS,
        elapsedMs: Date.now() - startedAt,
      });
    } catch (err) {
      setScanError(err.message || 'QR lookup failed.');
      logScanDiag('handle_token_scan_error', {
        tokenSuffix: String(token || '').slice(-8),
        error: safeErrorString(err?.message || err),
        elapsedMs: Date.now() - startedAt,
      });
    }
  }, [addScannedItem, fetchActiveCandidates, findLikelyActiveMatch, resolveActiveItemForToken, resolveQrToken, saleDiscPct, scanItems, scanModalOpen, selectedSaleId, setSelectedSaleId, setSelectedSectionId, stopScanner, toNumberOrNull]);

  useEffect(() => {
    handleTokenScanRef.current = handleTokenScan;
  }, [handleTokenScan]);

  const scanViaNative = useCallback(async (sessionId) => {
    const runtime = getCapacitorRuntime();
    const plugin = getNativeBarcodeScannerPlugin();
    if (!runtime || !plugin) {
      logScanDiag('native_scan_unavailable', {
        sessionId,
        hasRuntime: Boolean(runtime),
        hasPlugin: Boolean(plugin),
      });
      return { status: 'unavailable' };
    }

    const getPermissionGranted = (payload) => {
      if (!payload || typeof payload !== 'object') return false;
      if (payload.granted === true) return true;
      if (payload.camera === 'granted') return true;
      if (payload.status === 'granted') return true;
      return false;
    };

    try {
      if (typeof plugin.checkPermission === 'function') {
        const check = await plugin.checkPermission().catch(() => null);
        if (!getPermissionGranted(check) && typeof plugin.requestPermission === 'function') {
          const request = await plugin.requestPermission().catch(() => null);
          if (!getPermissionGranted(request)) {
            return { status: 'error', message: 'Camera access is required. Please enable it in your browser settings.' };
          }
        }
      } else if (typeof plugin.requestPermission === 'function') {
        const request = await plugin.requestPermission().catch(() => null);
        if (!getPermissionGranted(request)) {
          return { status: 'error', message: 'Camera access is required. Please enable it in your browser settings.' };
        }
      }

      let rawResult = null;
      if (typeof plugin.scanBarcodes === 'function') {
        try {
          rawResult = await plugin.scanBarcodes({ formats: ['QR_CODE'] });
        } catch {
          rawResult = await plugin.scanBarcodes();
        }
      } else if (typeof plugin.scan === 'function') {
        rawResult = await plugin.scan();
      } else if (typeof plugin.startScan === 'function') {
        if (typeof plugin.hideBackground === 'function') {
          try {
            await plugin.hideBackground();
          } catch {
          }
        }
        try {
          rawResult = await plugin.startScan();
        } finally {
          if (typeof plugin.showBackground === 'function') {
            try {
              await plugin.showBackground();
            } catch {
            }
          }
        }
      } else {
        logScanDiag('native_scan_unavailable_api', { sessionId });
        return { status: 'unavailable' };
      }

      if (isNativeScanCancelledResult(rawResult)) {
        logScanDiag('native_scan_cancelled', { sessionId });
        return { status: 'cancelled' };
      }

      const token = extractNativeScanToken(rawResult);
      if (!token) {
        logScanDiag('native_scan_no_token', {
          sessionId,
          resultKeys: rawResult && typeof rawResult === 'object' ? Object.keys(rawResult) : [],
        });
        return { status: 'empty' };
      }

      logScanDiag('native_scan_success', {
        sessionId,
        tokenSuffix: String(token).slice(-8),
      });
      return { status: 'scanned', token };
    } catch (err) {
      if (isNativeScanCancelledError(err)) {
        logScanDiag('native_scan_cancelled_error', {
          sessionId,
          errorName: String(err?.name || ''),
          errorMessage: safeErrorString(err?.message || err),
        });
        return { status: 'cancelled' };
      }
      logScanDiag('native_scan_error', {
        sessionId,
        errorName: String(err?.name || ''),
        errorMessage: safeErrorString(err?.message || err),
      });
      return {
        status: 'error',
        message: safeErrorString(err?.message || err || 'Native scanner failed'),
      };
    }
  }, []);

  const scanViaWeb = useCallback((sessionId, reason) => {
    logScanDiag('web_scan_fallback_start', {
      sessionId,
      reason,
    });
    setShowScanner(true);
  }, []);

  useEffect(() => {
    if (!showScanner) return undefined;

    const mySession = scannerSessionRef.current;
    const sessionId = `scan-${mySession}-${Date.now().toString(36)}`;
    let cancelled = false;
    const startedAt = Date.now();
    let removeVideoListeners = () => {};
    let stopFrameSampler = () => {};
    let stopCameraHeartbeat = () => {};
    let removePageListeners = () => {};
    let clearNoHitTimeout = () => {};

    const startScanner = async () => {
      setScanError('');
      setScanInProgress(true);
      scanGuardRef.current = false;
      decodeAttemptsRef.current = 0;
      decodeHitsRef.current = 0;
      decodeErrorSampleRef.current = '';
      decodeNoQrCountRef.current = 0;
      decodeOtherErrorCountRef.current = 0;
      decodeAttemptsSinceHitRef.current = 0;
      scanSessionDiagRef.current = {
        sessionId,
        startedAt,
        ended: false,
        autoSwitches: 0,
        iosNoHitFallbackSwitches: 0,
        activeCameraIndex: -1,
        candidateCount: 0,
        activeCameraId: '',
        activeCameraLabel: '',
        frameCallbacks: 0,
        frameLogEmits: 0,
        videoEventCounts: {},
      };
      logScanDiag('session_start', {
        sessionId,
        startedAtIso: new Date(startedAt).toISOString(),
        location: window.location.href,
        userAgent: navigator.userAgent,
        workerPath: QR_SCANNER_WORKER_PATH,
        barcodeDetectorAvailable: typeof window !== 'undefined' && typeof window.BarcodeDetector !== 'undefined',
        hardwareConcurrency: navigator.hardwareConcurrency || null,
        deviceMemory: navigator.deviceMemory || null,
        language: navigator.language || '',
        languages: Array.isArray(navigator.languages) ? navigator.languages : [],
        platform: navigator.platform || '',
        online: navigator.onLine,
        visibilityState: document.visibilityState,
      });
      try {
        if (navigator.permissions?.query) {
          const result = await navigator.permissions.query({ name: 'camera' });
          logScanDiag('permission_state', {
            sessionId,
            state: result?.state || 'unknown',
          });
        }
      } catch (err) {
        logScanDiag('permission_state_error', {
          sessionId,
          errorName: String(err?.name || ''),
          errorMessage: safeErrorString(err?.message || err),
        });
      }
      const onVisibility = () => {
        logScanDiag('page_visibility_change', {
          sessionId,
          visibilityState: document.visibilityState,
          hidden: document.hidden,
          elapsedMs: Date.now() - startedAt,
        });
      };
      const onOrientation = () => {
        const screenOrientation = window.screen?.orientation || null;
        logScanDiag('orientation_change', {
          sessionId,
          angle: screenOrientation?.angle ?? window.orientation ?? null,
          type: screenOrientation?.type || null,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          elapsedMs: Date.now() - startedAt,
        });
      };
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('orientationchange', onOrientation);
      removePageListeners = () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('orientationchange', onOrientation);
      };

      const container = document.getElementById('qr-scan-reader');
      if (!container) {
        setScanError('Scanner container not found.');
        setShowScanner(false);
        setScanInProgress(false);
        logScanDiag('session_error', {
          sessionId,
          error: 'scanner_container_missing',
          elapsedMs: Date.now() - startedAt,
        });
        emitSessionSummary('scanner_container_missing');
        scanSessionDiagRef.current = null;
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setScanError('Camera API not supported on this browser.');
        setShowScanner(false);
        setScanInProgress(false);
        logScanDiag('session_error', {
          sessionId,
          error: 'camera_api_not_supported',
          elapsedMs: Date.now() - startedAt,
        });
        emitSessionSummary('camera_api_not_supported');
        scanSessionDiagRef.current = null;
        return;
      }

      let warmupStream = null;
      try {
        warmupStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        const tracks = warmupStream
          .getTracks()
          .map((track) => ({
            kind: track.kind,
            label: track.label,
            readyState: track.readyState,
            settings: summarizeTrackSettings(track.getSettings?.() || {}),
            capabilities: summarizeTrackCapabilities(track.getCapabilities?.() || {}),
          }));
        logScanDiag('permission_warmup_success', { sessionId, tracks });
      } catch (err) {
        const message = formatCameraStartError(err);
        setScanError(message);
        setShowScanner(false);
        setScanInProgress(false);
        logScanDiag('permission_warmup_error', {
          sessionId,
          errorName: String(err?.name || ''),
          errorMessage: safeErrorString(err?.message || err),
          elapsedMs: Date.now() - startedAt,
        });
        emitSessionSummary('permission_warmup_error', {
          errorName: String(err?.name || ''),
          errorMessage: safeErrorString(err?.message || err),
        });
        scanSessionDiagRef.current = null;
        return;
      } finally {
        if (warmupStream) {
          warmupStream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch {
            }
          });
        }
      }

      if (cancelled || mySession !== scannerSessionRef.current) return;

      let cameras = [];
      try {
        cameras = await QrScanner.listCameras(true);
      } catch {
      }
      const cameraCandidates = buildRearCameraCandidates(cameras) || [];
      const orderedCameras = [
        ...cameraCandidates,
        ...cameras.filter((camera) => !cameraCandidates.some((candidate) => candidate.id === camera.id)),
      ];
      const camerasForLog = orderedCameras.map((camera, index) => ({
        id: camera.id,
        label: camera.label || `Camera ${index + 1}`,
      }));
      const lockedCameraId = modalLockedCameraIdRef.current;
      const lockedCameraLabel = String(modalLockedCameraLabelRef.current || '').toLowerCase();
      let lockedCamera = null;
      if (lockedCameraId) {
        lockedCamera = orderedCameras.find((camera) => camera.id === lockedCameraId) || null;
      }
      if (!lockedCamera && lockedCameraLabel) {
        lockedCamera = orderedCameras.find(
          (camera) => String(camera.label || '').toLowerCase() === lockedCameraLabel
        ) || null;
      }
      if (lockedCamera) {
        modalLockedCameraIdRef.current = lockedCamera.id;
        modalLockedCameraLabelRef.current = lockedCamera.label || modalLockedCameraLabelRef.current;
      }
      if ((lockedCameraId || lockedCameraLabel) && !lockedCamera) {
        logScanDiag('camera_lock_missing_from_candidates', {
          sessionId,
          lockedCameraId: lockedCameraId || null,
          lockedCameraLabel: modalLockedCameraLabelRef.current || null,
          candidateCount: cameraCandidates.length,
        });
      }
      const preferredCamera = lockedCamera || pickPreferredBackCamera(cameras);
      logScanDiag('devices_listed', {
        sessionId,
        count: cameras.length,
        devices: camerasForLog,
      });
      logScanDiag('camera_candidates', {
        sessionId,
        candidates: cameraCandidates.map((camera) => ({
          id: camera.id,
          label: camera.label || '',
          rank: rankBackCamera(camera),
        })),
      });
      logScanDiag('camera_pick', {
        sessionId,
        pickedDeviceId: preferredCamera?.id || null,
        pickedLabel: preferredCamera?.label || '',
        pickedFromModalLock: Boolean(lockedCamera),
        modalLockCameraId: modalLockedCameraIdRef.current || null,
        modalLockCameraLabel: modalLockedCameraLabelRef.current || null,
      });
      debugScanConsole('[SoldScan] available cameras', camerasForLog);

      container.innerHTML = '';
      const videoEl = document.createElement('video');
      videoEl.setAttribute('playsinline', 'true');
      videoEl.playsInline = true;
      videoEl.muted = true;
      videoEl.autoplay = true;
      videoEl.style.width = '100%';
      videoEl.style.height = 'auto';
      videoEl.style.display = 'block';
      container.appendChild(videoEl);
      scannerVideoElRef.current = videoEl;
      logScanDiag('video_element_created', {
        sessionId,
        playsInline: videoEl.playsInline,
        muted: videoEl.muted,
        autoplay: videoEl.autoplay,
      });

      const videoEventNames = [
        'loadedmetadata',
        'loadeddata',
        'canplay',
        'playing',
        'waiting',
        'stalled',
        'pause',
        'ended',
        'error',
        'resize',
      ];
      const videoEventHandlers = [];
      const onVideoEvent = (eventName) => {
        const sessionDiag = scanSessionDiagRef.current;
        if (sessionDiag) {
          const counts = sessionDiag.videoEventCounts || {};
          const count = (counts[eventName] || 0) + 1;
          counts[eventName] = count;
          sessionDiag.videoEventCounts = counts;
          if (count <= 3 || eventName === 'error' || eventName === 'stalled' || eventName === 'waiting') {
            logScanDiag('video_event', {
              sessionId,
              eventName,
              count,
              readyState: videoEl.readyState,
              networkState: videoEl.networkState,
              videoWidth: videoEl.videoWidth,
              videoHeight: videoEl.videoHeight,
              currentTime: videoEl.currentTime,
            });
          }
        }
      };
      videoEventNames.forEach((eventName) => {
        const handler = () => onVideoEvent(eventName);
        videoEl.addEventListener(eventName, handler);
        videoEventHandlers.push([eventName, handler]);
      });
      removeVideoListeners = () => {
        videoEventHandlers.forEach(([eventName, handler]) => {
          try {
            videoEl.removeEventListener(eventName, handler);
          } catch {
          }
        });
      };

      if (typeof videoEl.requestVideoFrameCallback === 'function') {
        let frameSamplerCancelled = false;
        const frameState = {
          lastNow: 0,
          lastMediaTime: 0,
          lastPresentedFrames: null,
        };
        const onFrame = (now, metadata) => {
          if (cancelled || frameSamplerCancelled) return;
          const sessionDiag = scanSessionDiagRef.current;
          if (sessionDiag) {
            sessionDiag.frameCallbacks = (sessionDiag.frameCallbacks || 0) + 1;
            const frameCount = sessionDiag.frameCallbacks;
            const deltaNowMs = frameState.lastNow ? Number((now - frameState.lastNow).toFixed(2)) : null;
            const mediaDelta = frameState.lastMediaTime
              ? Number((metadata.mediaTime - frameState.lastMediaTime).toFixed(4))
              : null;
            const presentedDelta = frameState.lastPresentedFrames !== null
              ? metadata.presentedFrames - frameState.lastPresentedFrames
              : null;
            if (frameCount <= 3 || frameCount % 120 === 0) {
              sessionDiag.frameLogEmits = (sessionDiag.frameLogEmits || 0) + 1;
              logScanDiag('video_frame_sample', {
                sessionId,
                frameCount,
                presentedFrames: metadata.presentedFrames,
                mediaTime: metadata.mediaTime,
                expectedDisplayTime: metadata.expectedDisplayTime || null,
                processingDuration: metadata.processingDuration || null,
                deltaNowMs,
                mediaDelta,
                presentedDelta,
              });
            }
            frameState.lastNow = now;
            frameState.lastMediaTime = metadata.mediaTime;
            frameState.lastPresentedFrames = metadata.presentedFrames;
          }
          videoEl.requestVideoFrameCallback(onFrame);
        };
        videoEl.requestVideoFrameCallback(onFrame);
        stopFrameSampler = () => {
          frameSamplerCancelled = true;
        };
      } else {
        logScanDiag('video_frame_callback_unsupported', {
          sessionId,
        });
      }

      let activeCameraIndex = Math.max(
        0,
        cameraCandidates.findIndex((camera) => camera.id === preferredCamera?.id)
      );
      const cameraLockedForModal = Boolean(
        modalLockedCameraIdRef.current || modalLockedCameraLabelRef.current
      );
      const autoSwitchAllowed = QR_AUTO_SWITCH_ENABLED && !isIOSWebkit() && !cameraLockedForModal;
      const iosNoHitSwitchAllowed = Boolean(
        QR_IOS_NO_HIT_SWITCH_ENABLED &&
        isIOSWebkit() &&
        !autoSwitchAllowed &&
        !cameraLockedForModal &&
        cameraCandidates.length > 1
      );
      logScanDiag('auto_switch_policy', {
        sessionId,
        autoSwitchAllowed,
        iosNoHitSwitchAllowed,
        cameraLockedForModal,
        modalLockCameraId: modalLockedCameraIdRef.current || null,
        iosNoHitSwitchTriggerAttempts: QR_IOS_NO_HIT_SWITCH_TRIGGER_ATTEMPTS,
        platform: navigator.platform || '',
        userAgent: navigator.userAgent,
      });
      if (scanSessionDiagRef.current) {
        scanSessionDiagRef.current.activeCameraIndex = activeCameraIndex;
        scanSessionDiagRef.current.candidateCount = cameraCandidates.length;
        scanSessionDiagRef.current.activeCameraId = cameraCandidates[activeCameraIndex]?.id || preferredCamera?.id || '';
        scanSessionDiagRef.current.activeCameraLabel = cameraCandidates[activeCameraIndex]?.label || preferredCamera?.label || '';
      }
      let cameraSwitchInFlight = false;
      let scannerInstance = null;
      let autoSwitchCount = 0;
      let iosNoHitSwitchDone = false;
      let noHitTimeoutId = null;
      const scheduleNoHitTimeout = () => {
        if (noHitTimeoutId) {
          clearTimeout(noHitTimeoutId);
        }
        noHitTimeoutId = setTimeout(() => {
          if (cancelled || mySession !== scannerSessionRef.current) return;
          logScanDiag('scan_no_hit_timeout', {
            sessionId,
            elapsedMs: Date.now() - startedAt,
            attempts: decodeAttemptsRef.current,
            hits: decodeHitsRef.current,
            noQrCount: decodeNoQrCountRef.current,
            activeCameraId: scanSessionDiagRef.current?.activeCameraId || null,
            activeCameraLabel: scanSessionDiagRef.current?.activeCameraLabel || '',
          });
          setScanError('QR code not scanned. Try again.');
          void stopScanner({ reason: 'scan_no_hit_timeout' });
        }, QR_NO_HIT_TIMEOUT_MS);
      };
      clearNoHitTimeout = () => {
        if (noHitTimeoutId) {
          clearTimeout(noHitTimeoutId);
          noHitTimeoutId = null;
        }
      };

      const tryAutoSwitchCamera = async (reason) => {
        if (!autoSwitchAllowed) {
          logScanDiag('camera_auto_switch_skipped_policy', {
            sessionId,
            reason,
            activeIndex: activeCameraIndex,
            activeDeviceId: cameraCandidates[activeCameraIndex]?.id || null,
            activeLabel: cameraCandidates[activeCameraIndex]?.label || '',
          });
          return;
        }
        if (cameraSwitchInFlight) return;
        if (!scannerInstance || !cameraCandidates.length) return;
        if (autoSwitchCount >= QR_AUTO_SWITCH_MAX) {
          logScanDiag('camera_auto_switch_skipped_limit', {
            sessionId,
            reason,
            autoSwitchCount,
            autoSwitchLimit: QR_AUTO_SWITCH_MAX,
            activeIndex: activeCameraIndex,
          });
          return;
        }
        const nextIndex = activeCameraIndex + 1;
        if (nextIndex >= cameraCandidates.length) return;

        cameraSwitchInFlight = true;
        const nextCamera = cameraCandidates[nextIndex];
        logScanDiag('camera_auto_switch_requested', {
          sessionId,
          reason,
          fromIndex: activeCameraIndex,
          toIndex: nextIndex,
          fromDeviceId: cameraCandidates[activeCameraIndex]?.id || null,
          toDeviceId: nextCamera?.id || null,
          toLabel: nextCamera?.label || '',
          attempts: decodeAttemptsRef.current,
          hits: decodeHitsRef.current,
        });
        try {
          await scannerInstance.setCamera(nextCamera.id);
          activeCameraIndex = nextIndex;
          autoSwitchCount += 1;
          if (scanSessionDiagRef.current) {
            scanSessionDiagRef.current.autoSwitches += 1;
            scanSessionDiagRef.current.activeCameraIndex = activeCameraIndex;
            scanSessionDiagRef.current.activeCameraId = nextCamera.id;
            scanSessionDiagRef.current.activeCameraLabel = nextCamera.label || '';
          }
          decodeAttemptsRef.current = 0;
          decodeErrorSampleRef.current = '';
          await tuneVideoTrackForScan(videoEl, sessionId, 'auto_camera_switch');
          const postSwitchSettings = getActiveVideoTrack(videoEl)?.getSettings?.() || {};
          logScanDiag('camera_auto_switch_success', {
            sessionId,
            activeIndex: activeCameraIndex,
            activeDeviceId: nextCamera.id,
            activeLabel: nextCamera.label || '',
            settings: summarizeTrackSettings(postSwitchSettings),
          });
        } catch (err) {
          logScanDiag('camera_auto_switch_failed', {
            sessionId,
            toDeviceId: nextCamera?.id || null,
            errorName: String(err?.name || ''),
            errorMessage: safeErrorString(err?.message || err),
          });
        } finally {
          cameraSwitchInFlight = false;
        }
      };

      const tryIosNoHitSwitch = async (reason) => {
        if (!iosNoHitSwitchAllowed || iosNoHitSwitchDone) return;
        if (cameraSwitchInFlight) return;
        if (!scannerInstance || !cameraCandidates.length) return;
        const nextIndex = activeCameraIndex + 1;
        if (nextIndex >= cameraCandidates.length) {
          iosNoHitSwitchDone = true;
          logScanDiag('ios_no_hit_switch_skipped_exhausted', {
            sessionId,
            reason,
            activeIndex: activeCameraIndex,
            candidateCount: cameraCandidates.length,
          });
          return;
        }

        cameraSwitchInFlight = true;
        const nextCamera = cameraCandidates[nextIndex];
        logScanDiag('ios_no_hit_switch_requested', {
          sessionId,
          reason,
          fromIndex: activeCameraIndex,
          toIndex: nextIndex,
          fromDeviceId: cameraCandidates[activeCameraIndex]?.id || null,
          toDeviceId: nextCamera?.id || null,
          toLabel: nextCamera?.label || '',
          attempts: decodeAttemptsRef.current,
          noQrCount: decodeNoQrCountRef.current,
        });
        try {
          await scannerInstance.setCamera(nextCamera.id);
          activeCameraIndex = nextIndex;
          iosNoHitSwitchDone = true;
          decodeAttemptsSinceHitRef.current = 0;
          if (scanSessionDiagRef.current) {
            scanSessionDiagRef.current.iosNoHitFallbackSwitches =
              (scanSessionDiagRef.current.iosNoHitFallbackSwitches || 0) + 1;
            scanSessionDiagRef.current.activeCameraIndex = activeCameraIndex;
            scanSessionDiagRef.current.activeCameraId = nextCamera.id;
            scanSessionDiagRef.current.activeCameraLabel = nextCamera.label || '';
          }
          await tuneVideoTrackForScan(videoEl, sessionId, 'ios_no_hit_switch');
          const postSwitchSettings = getActiveVideoTrack(videoEl)?.getSettings?.() || {};
          logScanDiag('ios_no_hit_switch_success', {
            sessionId,
            activeIndex: activeCameraIndex,
            activeDeviceId: nextCamera.id,
            activeLabel: nextCamera.label || '',
            settings: summarizeTrackSettings(postSwitchSettings),
          });
        } catch (err) {
          logScanDiag('ios_no_hit_switch_failed', {
            sessionId,
            toDeviceId: nextCamera?.id || null,
            errorName: String(err?.name || ''),
            errorMessage: safeErrorString(err?.message || err),
          });
        } finally {
          cameraSwitchInFlight = false;
        }
      };

      let lastScanRegionKey = '';
      const calculateScanRegionWithDiag = (video) => {
        const region = calculateHighFidelityScanRegion(video);
        const key = `${video.videoWidth}x${video.videoHeight}:${region.width}x${region.height}:${region.downScaledWidth}x${region.downScaledHeight}`;
        if (key !== lastScanRegionKey) {
          lastScanRegionKey = key;
          logScanDiag('scan_region', {
            sessionId,
            region,
            videoWidth: video.videoWidth || 0,
            videoHeight: video.videoHeight || 0,
          });
        }
        return region;
      };

      const scanner = new QrScanner(
        videoEl,
        (decodedResult) => {
          if (cancelled || mySession !== scannerSessionRef.current) return;
          const decodedText = extractDecodedText(decodedResult);
          const token = parseQrToken(decodedText);
          if (!token) return;

          const now = Date.now();
          const lastSeenAt = lastTokenScanAtRef.current.get(token) || 0;
          if (now - lastSeenAt < QR_DUPLICATE_COOLDOWN_MS) {
            logScanDiag('decode_hit_ignored_cooldown', {
              sessionId,
              tokenSuffix: String(token).slice(-8),
              deltaMs: now - lastSeenAt,
            });
            return;
          }
          if (scanGuardRef.current) {
            logScanDiag('decode_hit_ignored_busy', {
              sessionId,
              tokenSuffix: String(token).slice(-8),
            });
            return;
          }

          lastTokenScanAtRef.current.set(token, now);
          scanGuardRef.current = true;
          decodeHitsRef.current += 1;
          decodeAttemptsSinceHitRef.current = 0;
          scheduleNoHitTimeout();
          const activeCameraId = scanSessionDiagRef.current?.activeCameraId || '';
          debugScanConsole('[SoldScan] decode hit', { source: 'qr-scanner', token });
          logScanDiag('decode_hit', {
            sessionId,
            source: 'qr-scanner',
            token,
            tokenSuffix: String(token).slice(-8),
            activeCameraId: activeCameraId || null,
            elapsedMs: Date.now() - startedAt,
          });
          const tokenHandler = handleTokenScanRef.current;
          if (typeof tokenHandler !== 'function') {
            scanGuardRef.current = false;
            return;
          }
          Promise.resolve(tokenHandler(token))
            .catch((err) => {
              logScanDiag('decode_hit_error', {
                sessionId,
                tokenSuffix: String(token).slice(-8),
                error: safeErrorString(err?.message || err),
              });
            })
            .finally(() => {
              scanGuardRef.current = false;
            });
        },
        {
          preferredCamera: preferredCamera?.id || 'environment',
          maxScansPerSecond: 25,
          onDecodeError: (err) => {
            const message = safeErrorString(err?.message || err);
            decodeAttemptsRef.current += 1;
            const isNoQr = isNoQrDecodeMessage(message);
            decodeAttemptsSinceHitRef.current += 1;
            if (isNoQr) {
              decodeNoQrCountRef.current += 1;
            } else {
              decodeOtherErrorCountRef.current += 1;
            }
            if (message && !isNoQr) {
              decodeErrorSampleRef.current = message;
              if (decodeOtherErrorCountRef.current <= 5) {
                logScanDiag('decode_error_detail', {
                  sessionId,
                  attempts: decodeAttemptsRef.current,
                  errorName: String(err?.name || ''),
                  errorMessage: message,
                });
              }
            }
            if (decodeAttemptsRef.current % 40 === 0) {
              logScanDiag('decode_progress', {
                sessionId,
                attempts: decodeAttemptsRef.current,
                hits: decodeHitsRef.current,
                noQrCount: decodeNoQrCountRef.current,
                otherErrorCount: decodeOtherErrorCountRef.current,
                sample: decodeErrorSampleRef.current || message,
                elapsedMs: Date.now() - startedAt,
              });
            }
            if (
              autoSwitchAllowed &&
              decodeHitsRef.current === 0 &&
              decodeAttemptsRef.current > 0 &&
              decodeAttemptsRef.current % QR_AUTO_SWITCH_TRIGGER_ATTEMPTS === 0
            ) {
              void tryAutoSwitchCamera('no_hits');
            }
            if (
              iosNoHitSwitchAllowed &&
              !iosNoHitSwitchDone &&
              decodeHitsRef.current === 0 &&
              decodeAttemptsRef.current > 0 &&
              decodeAttemptsRef.current % QR_IOS_NO_HIT_SWITCH_TRIGGER_ATTEMPTS === 0
            ) {
              void tryIosNoHitSwitch('no_hits');
            }
            if (
              !autoSwitchAllowed &&
              !isIOSWebkit() &&
              decodeAttemptsSinceHitRef.current > 0 &&
              decodeAttemptsSinceHitRef.current % QR_REFOCUS_TRIGGER_ATTEMPTS === 0
            ) {
              logScanDiag('camera_refocus_requested', {
                sessionId,
                attemptsSinceHit: decodeAttemptsSinceHitRef.current,
                totalAttempts: decodeAttemptsRef.current,
                hits: decodeHitsRef.current,
              });
              void tuneVideoTrackForScan(
                videoEl,
                sessionId,
                decodeHitsRef.current > 0 ? 'post_hit_refocus' : 'no_hit_refocus'
              );
            }
          },
          calculateScanRegion: calculateScanRegionWithDiag,
          returnDetailedScanResult: true,
          highlightScanRegion: false,
          highlightCodeOutline: false,
        }
      );
      scannerInstance = scanner;
      scannerRef.current = scanner;

      try {
        await scanner.start();
      } catch (err) {
        scannerRef.current = null;
        const message = formatCameraStartError(err);
        setScanError(message);
        setShowScanner(false);
        setScanInProgress(false);
        logScanDiag('camera_start_error', {
          sessionId,
          target: preferredCamera?.id || 'environment',
          errorName: String(err?.name || ''),
          errorMessage: safeErrorString(err?.message || err),
          elapsedMs: Date.now() - startedAt,
        });
        emitSessionSummary('camera_start_error', {
          errorName: String(err?.name || ''),
          errorMessage: safeErrorString(err?.message || err),
        });
        scanSessionDiagRef.current = null;
        return;
      }

      try {
        await scanner.setInversionMode('both');
      } catch {
      }
      await tuneVideoTrackForScan(videoEl, sessionId, 'post_start');

      if (cancelled || mySession !== scannerSessionRef.current) {
        stopScanner({ invalidateSession: false, hideScanner: false, reason: 'session_invalidated_before_ready' });
        return;
      }

      const track = getActiveVideoTrack(videoEl);
      const settings = track?.getSettings?.() || {};

      debugScanConsole('[SoldScan] camera started', {
        target: preferredCamera?.id || 'environment',
        settings,
        settingsJson: JSON.stringify(settings || {}),
      });
      logScanDiag('camera_started', {
        sessionId,
        target: preferredCamera?.id || 'environment',
        targetLabel: preferredCamera?.label || '',
        actualDeviceId: settings.deviceId || null,
        width: settings.width || null,
        height: settings.height || null,
        frameRate: settings.frameRate || null,
      });
      const heartbeatInterval = setInterval(() => {
        if (cancelled || mySession !== scannerSessionRef.current) return;
        const activeTrack = getActiveVideoTrack(videoEl);
        const heartbeatSettings = activeTrack?.getSettings?.() || {};
        logScanDiag('camera_heartbeat', {
          sessionId,
          elapsedMs: Date.now() - startedAt,
          attempts: decodeAttemptsRef.current,
          hits: decodeHitsRef.current,
          noQrCount: decodeNoQrCountRef.current,
          otherErrorCount: decodeOtherErrorCountRef.current,
          activeCameraIndex: scanSessionDiagRef.current?.activeCameraIndex ?? null,
          activeCameraId: scanSessionDiagRef.current?.activeCameraId || null,
          activeCameraLabel: scanSessionDiagRef.current?.activeCameraLabel || '',
          settings: summarizeTrackSettings(heartbeatSettings),
        });
      }, 5000);
      stopCameraHeartbeat = () => {
        clearInterval(heartbeatInterval);
      };
      scheduleNoHitTimeout();
      setScanInProgress(true);
    };

    startScanner();

    return () => {
      cancelled = true;
      removeVideoListeners();
      stopFrameSampler();
      stopCameraHeartbeat();
      removePageListeners();
      clearNoHitTimeout();
      stopScanner({ invalidateSession: false, hideScanner: false, reason: 'scanner_effect_cleanup' });
    };
  }, [emitSessionSummary, showScanner, stopScanner]);

  const openScanModal = useCallback(() => {
    modalLockedCameraIdRef.current = '';
    modalLockedCameraLabelRef.current = '';
    setScanModalOpen(true);
    setScanError('');
    logScanDiag('scan_modal_open');
  }, []);

  const closeScanModal = useCallback(() => {
    const activeSession = scanSessionDiagRef.current;
    logScanDiag('scan_modal_close', {
      scanItemsCount: scanItems.length,
      manualLinesCount: manualLines.length,
      showScanner,
      activeSessionId: activeSession?.sessionId || null,
    });
    stopScanner({ reason: 'close_scan_modal' });
    resetScanModal();
  }, [manualLines.length, resetScanModal, scanItems.length, showScanner, stopScanner]);

  const stopScan = useCallback(() => {
    const activeSession = scanSessionDiagRef.current;
    logScanDiag('scan_stop_requested', {
      scanItemsCount: scanItems.length,
      manualLinesCount: manualLines.length,
      activeSessionId: activeSession?.sessionId || null,
      attempts: decodeAttemptsRef.current,
      hits: decodeHitsRef.current,
      noQrCount: decodeNoQrCountRef.current,
      otherErrorCount: decodeOtherErrorCountRef.current,
    });
    stopScanner({ reason: 'stop_scan_button' });
    setShowScanner(false);
  }, [manualLines.length, scanItems.length, stopScanner]);

  const scanTotal = useMemo(() => {
    let total = 0;
    for (const item of scanItems) {
      const price = toNumberOrNull(item.soldPrice);
      if (price !== null && price > 0) {
        const qty = toIntOr(item.qtySold, 1);
        total += price * qty;
      }
    }
    for (const line of manualLines) {
      const price = toNumberOrNull(line.price);
      const qty = toIntOr(line.qty, 1);
      if (price !== null && price > 0) {
        total += price * qty;
      }
    }
    return total;
  }, [manualLines, scanItems, toIntOr, toNumberOrNull]);

  const hasMultipleSales = useMemo(() => {
    const saleIds = new Set(scanItems.map((item) => item.saleId).filter(Boolean));
    return saleIds.size > 1;
  }, [scanItems]);

  const addManualLine = () => {
    setScanError((prev) =>
      prev === INCOMPLETE_MANUAL_LINE_ERROR
        ? ''
        : prev
    );
    if (!manualOpen) setManualOpen(true);
    setManualLines((prev) => [...prev, createManualLine()]);
  };

  const updateManualLine = useCallback((id, field, value) => {
    setScanError((prev) =>
      prev === INCOMPLETE_MANUAL_LINE_ERROR
        ? ''
        : prev
    );
    setManualLines((prev) => {
      let didChange = false;
      const next = prev.map((row) => {
        if (row.id !== id) return row;
        if (row[field] === value) return row;
        didChange = true;
        return { ...row, [field]: value };
      });
      return didChange ? next : prev;
    });
  }, []);

  const removeManualLine = (id) => {
    setManualLines((prev) => prev.filter((row) => row.id !== id));
  };

  const openManualItems = () => {
    setScanError((prev) =>
      prev === INCOMPLETE_MANUAL_LINE_ERROR
        ? ''
        : prev
    );
    setManualOpen(true);
    setManualLines((prev) => (prev.length ? prev : [createManualLine()]));
  };

  const updateScanItem = (id, field, value) => {
    setScanItems((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
    if (field === 'qtySold' && id === scanErrorItemId) {
      setScanErrorItemId('');
      setScanError((prev) => (prev.startsWith('Maximum quantity of ') ? '' : prev));
    }
  };

  const removeScanItem = (id) => {
    setScanItems((prev) => prev.filter((row) => row.id !== id));
    if (id === scanErrorItemId) {
      setScanErrorItemId('');
      setScanError((prev) => (prev.startsWith('Maximum quantity of ') ? '' : prev));
    }
  };

  const startScan = async () => {
    setScanError('');
    if (showScanner || scanInProgress) return;
    const nextSession = scannerSessionRef.current + 1;
    logScanDiag('scan_start_requested', {
      previousSession: scannerSessionRef.current,
      nextSession,
      scanItemsCount: scanItems.length,
      manualLinesCount: manualLines.length,
    });
    scannerSessionRef.current = nextSession;
    scanGuardRef.current = false;
    lastTokenScanAtRef.current.clear();
    setScanInProgress(true);

    const nativeResult = await scanViaNative(nextSession);
    if (nextSession !== scannerSessionRef.current) {
      setScanInProgress(false);
      return;
    }

    if (nativeResult.status === 'scanned') {
      setShowScanner(false);
      setScanInProgress(false);
      const tokenHandler = handleTokenScanRef.current;
      if (typeof tokenHandler === 'function') {
        await tokenHandler(nativeResult.token);
      }
      return;
    }

    if (nativeResult.status === 'error' && nativeResult.message) {
      logScanDiag('native_scan_error_fallback_web', {
        sessionId: nextSession,
        errorMessage: nativeResult.message,
      });
    }

    setScanInProgress(false);
    scanViaWeb(nextSession, nativeResult.status);
  };

  const buildManualItemsForConfirm = useCallback(() => {
    return manualLines
      .map((row) => {
        const title = String(row.title || '').trim();
        const category = String(row.category || '').trim();
        const rawPrice = String(row.price ?? '').trim();
        const inventoryItemId = String(row.inventoryItemId || '').trim();
        return {
          inventoryItemId,
          title,
          category,
          quantity: toIntOr(row.qty, 1),
          price: toNumberOrNull(row.price),
          hasInput: Boolean(inventoryItemId || title || category || rawPrice),
        };
      })
      .filter((row) => row.hasInput)
      .map(({ hasInput, ...row }) => row);
  }, [manualLines, toIntOr, toNumberOrNull]);

  const buildActiveItemIndexes = useCallback((activeRows) => {
    const normalizeTitle = (value) => String(value || '').trim().toLowerCase();
    const activeByTitle = new Map();
    const activeById = new Map();

    for (const row of activeRows) {
      const status = String(row?.status || '').trim().toLowerCase();
      if (status && status !== 'active' && status !== 'on hold') continue;
      if (row?.id) {
        activeById.set(String(row.id), row);
      }
      const key = normalizeTitle(row?.title);
      if (!key) continue;
      const existing = activeByTitle.get(key) || [];
      existing.push(row);
      activeByTitle.set(key, existing);
    }

    return { activeByTitle, activeById };
  }, []);

  const reconcileManualItemsForConfirm = useCallback(({
    manualItems,
    activeById,
    activeByTitle,
    scannedItemIds,
    selectedSaleIdForMatch,
  }) => {
    const normalizeTitle = (value) => String(value || '').trim().toLowerCase();
    const matchedInventorySales = new Map();
    const manualItemsToInsert = [];
    const transactionLines = [];

    const pushManualInsert = (manualItem) => {
      const soldUnitPrice = toNumberOrNull(manualItem.price) || 0;
      const quantity = toIntOr(manualItem.quantity, 1);
      const lineSubtotal = Number((soldUnitPrice * quantity).toFixed(2));
      const manualTitle = String(manualItem.title || '').trim() || MANUAL_ITEM_DEFAULT_TITLE;
      transactionLines.push({
        inventory_item_id: null,
        title: manualTitle,
        category: String(manualItem.category || '').trim(),
        quantity,
        asking_unit_price: soldUnitPrice,
        sold_unit_price: soldUnitPrice,
        line_subtotal: lineSubtotal,
        is_manual: true,
      });
      manualItemsToInsert.push({
        title: manualTitle,
        category: manualItem.category,
        quantity: manualItem.quantity,
        price: manualItem.price,
      });
    };

    const pushMatchedSale = (match, manualItem, titleValue) => {
      if (!match?.id) {
        pushManualInsert(manualItem);
        return null;
      }
      if (scannedItemIds.has(match.id)) {
        return {
          errorMessage: `"${titleValue}" is already in scanned items. Remove duplicate manual line.`,
        };
      }

      const qtyAvailable = toIntOr(match.quantity_estimate, 1);
      const qty = toIntOr(manualItem.quantity, 1);
      if (qty > qtyAvailable) {
        return {
          errorMessage: `"${titleValue}" qty exceeds available (${qtyAvailable}).`,
        };
      }

      const enteredPrice = toNumberOrNull(manualItem.price);
      const listPrice = toNumberOrNull(match.per_item_cost_estimate);
      const discountPct = normalizePercent(saleDiscPct);
      const discountedPrice = calculateCheckoutUnitPrice(listPrice, discountPct);
      // If manual matched line still carries tag price, normalize it to current discounted price.
      const normalizedUnitPrice = (
        discountPct > 0
        && enteredPrice !== null
        && listPrice !== null
        && discountedPrice !== null
        && Math.abs(enteredPrice - listPrice) < 0.005
      )
        ? discountedPrice
        : enteredPrice;
      const lineTotal = Number(((normalizedUnitPrice || 0) * qty).toFixed(2));
      transactionLines.push({
        inventory_item_id: match.id,
        title: String(manualItem.title || match.title || titleValue || '').trim() || 'Manual Item',
        category: String(manualItem.category || match.category || '').trim(),
        quantity: qty,
        asking_unit_price: listPrice,
        sold_unit_price: normalizedUnitPrice,
        line_subtotal: lineTotal,
        is_manual: true,
      });
      const existing = matchedInventorySales.get(match.id);
      if (existing) {
        existing.qty += qty;
        existing.totalSoldPrice = Number((existing.totalSoldPrice + lineTotal).toFixed(2));
      } else {
        matchedInventorySales.set(match.id, {
          itemId: match.id,
          saleId: match.sale_id || selectedSaleIdForMatch,
          qty,
          totalSoldPrice: lineTotal,
          title: manualItem.title || match.title || '',
        });
      }

      return null;
    };

    for (const manualItem of manualItems) {
      if (manualItem.inventoryItemId) {
        const match = activeById.get(String(manualItem.inventoryItemId));
        const titleValue = manualItem.title || 'Selected item';
        const error = pushMatchedSale(match, manualItem, titleValue);
        if (error) return { ...error, matchedInventorySales, manualItemsToInsert, transactionLines };
        continue;
      }

      const key = normalizeTitle(manualItem.title);
      const matches = activeByTitle.get(key) || [];
      if (matches.length === 1) {
        const error = pushMatchedSale(matches[0], manualItem, manualItem.title);
        if (error) return { ...error, matchedInventorySales, manualItemsToInsert, transactionLines };
        continue;
      }
      if (matches.length > 1) {
        return {
          errorMessage: `"${manualItem.title}" matches multiple inventory items. Scan the tag or use a unique title.`,
          matchedInventorySales,
          manualItemsToInsert,
          transactionLines,
        };
      }
      pushManualInsert(manualItem);
    }

    return {
      errorMessage: '',
      matchedInventorySales,
      manualItemsToInsert,
      transactionLines,
    };
  }, [saleDiscPct, toIntOr, toNumberOrNull]);

  const recordInventoryItemSale = useCallback(async ({
    itemId,
    saleId,
    qtySold,
    totalSoldPrice,
    fallbackErrorMessage,
  }) => {
    const resp = await fetchWrapper(
      `${apiUrl}/supabase/rpc/record_inventory_item_sale`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_id: itemId,
          p_sale_id: saleId,
          p_client_id: clientId,
          p_qty_sold: qtySold,
          p_total_sold_price: totalSoldPrice,
        }),
      }
    );
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData.error?.message || fallbackErrorMessage);
    }
  }, [apiUrl, clientId, fetchWrapper]);

  const persistMatchedManualSales = useCallback(async (matchedInventorySales) => {
    for (const matched of matchedInventorySales.values()) {
      await recordInventoryItemSale({
        itemId: matched.itemId,
        saleId: matched.saleId,
        qtySold: matched.qty,
        totalSoldPrice: matched.totalSoldPrice,
        fallbackErrorMessage: 'Failed to record matched manual sale',
      });
    }
  }, [recordInventoryItemSale]);

  const persistInsertedManualItemSales = useCallback(async (insertedItems, saleId) => {
    for (const item of insertedItems) {
      const qty = toIntOr(item.quantity_estimate || 1, 1);
      const price = toNumberOrNull(item.per_item_cost_estimate);
      if (!price || price <= 0) {
        throw new Error('Manual items need a valid price to mark sold.');
      }
      const total = price * qty;
      await recordInventoryItemSale({
        itemId: item.id,
        saleId,
        qtySold: qty,
        totalSoldPrice: total,
        fallbackErrorMessage: 'Failed to record manual sale',
      });
    }
  }, [recordInventoryItemSale, toIntOr, toNumberOrNull]);

  const persistScannedItemSales = useCallback(async (items, saleId) => {
    for (const item of items) {
      await recordInventoryItemSale({
        itemId: item.id,
        saleId: item.saleId || saleId,
        qtySold: toIntOr(item.qtySold, 1),
        totalSoldPrice: toNumberOrNull(item.soldPrice),
        fallbackErrorMessage: 'Failed to record sale',
      });
    }
  }, [recordInventoryItemSale, toIntOr, toNumberOrNull]);

  const fetchSalesTransactionMeta = useCallback(async ({
    saleId,
    salesTransId,
  }) => {
    const fallback = {
      id: String(salesTransId || '').trim(),
      saleTransNumber: null,
    };
    if (!fallback.id) return fallback;

    try {
      const response = await fetchWrapper(
        `${apiUrl}/supabase/rpc/select_sales_trans`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_client_id: clientId,
            p_sale_id: saleId || null,
          }),
        }
      );
      const data = await response?.json?.().catch(() => []);
      if (!response?.ok) return fallback;
      const rows = Array.isArray(data) ? data : [];
      const match = rows.find((row) => String(row?.id || '').trim() === fallback.id);
      const meta = normalizeSalesTransactionMeta(match);
      return {
        id: fallback.id,
        saleTransNumber: meta.saleTransNumber,
      };
    } catch (err) {
      logScanDiag('sales_trans_meta_fetch_failed', {
        salesTransId: fallback.id,
        error: safeErrorString(err?.message || err),
      });
      return fallback;
    }
  }, [apiUrl, clientId, fetchWrapper]);

  const createSalesTransaction = useCallback(async ({
    saleId,
    checkoutSummary,
    itemLines,
  }) => {
    const useStripeCheckout = Boolean(checkoutSummary.useStripeCheckout);
    const headerResponse = await fetchWrapper(
      `${apiUrl}/supabase/rpc/insert_sales_trans`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_client_id: clientId,
          p_sale_id: saleId,
          p_created_by: clientId,
          p_payment_method: useStripeCheckout ? 'stripe_checkout' : checkoutSummary.paymentMethod,
          p_is_tax_exempt: checkoutSummary.isTaxExempt,
          p_tax_exempt_business_name: checkoutSummary.taxExemptBusinessName,
          p_tax_exempt_tax_id: checkoutSummary.taxExemptTaxId,
          p_discount_pct_applied: checkoutSummary.discountPctApplied,
          p_sales_tax_pct_applied: checkoutSummary.salesTaxPctApplied,
          p_card_fee_pct_applied: checkoutSummary.cardFeePctApplied,
          p_subtotal_amount: checkoutSummary.subtotalAmount,
          p_tax_amount: checkoutSummary.taxAmount,
          p_card_fee_amount: checkoutSummary.cardFeeAmount,
          p_total_amount: checkoutSummary.totalAmount,
          p_item_count: checkoutSummary.itemCount,
          p_status: useStripeCheckout ? 'pending_payment' : 'completed',
        }),
      }
    );
    if (!headerResponse.ok) {
      const errData = await headerResponse.json().catch(() => ({}));
      throw new Error(errData.error?.message || 'Failed to create sales transaction header');
    }
    const headerResult = await headerResponse.json();
    let transactionMeta = normalizeSalesTransactionMeta(headerResult);
    const normalizedTransId = transactionMeta.id;
    if (!normalizedTransId) {
      throw new Error('Sales transaction header did not return an ID.');
    }

    if (Array.isArray(itemLines) && itemLines.length > 0) {
      const linesResponse = await fetchWrapper(
        `${apiUrl}/supabase/rpc/insert_sales_trans_items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_sales_trans_id: normalizedTransId,
            p_client_id: clientId,
            p_sale_id: saleId,
            p_items: itemLines,
          }),
        }
      );
      if (!linesResponse.ok) {
        const errData = await linesResponse.json().catch(() => ({}));
        throw new Error(errData.error?.message || 'Failed to create sales transaction items');
      }
    }

    if (useStripeCheckout) {
      const paymentResponse = await fetchWrapper(
        `${apiUrl}/stripe/create-sale-payment-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            salesTransId: normalizedTransId,
            saleId,
          }),
        }
      );
      const paymentResult = await paymentResponse.json().catch(() => ({}));
      if (!paymentResponse.ok) {
        throw new Error(paymentResult.error || 'Failed to create Stripe payment QR.');
      }
      transactionMeta = {
        ...transactionMeta,
        stripePaymentUrl: String(paymentResult.url || '').trim(),
        stripeSessionId: String(paymentResult.session_id || '').trim(),
      };
    }

    if (!transactionMeta.saleTransNumber) {
      const stripePaymentUrl = transactionMeta.stripePaymentUrl;
      const stripeSessionId = transactionMeta.stripeSessionId;
      const fetchedMeta = await fetchSalesTransactionMeta({
        saleId,
        salesTransId: normalizedTransId,
      });
      transactionMeta = {
        ...fetchedMeta,
        stripePaymentUrl: stripePaymentUrl || fetchedMeta.stripePaymentUrl || '',
        stripeSessionId: stripeSessionId || fetchedMeta.stripeSessionId || '',
      };
    }

    return transactionMeta;
  }, [apiUrl, clientId, fetchSalesTransactionMeta, fetchWrapper]);

  const handleConfirmScan = async (confirmOptions = {}) => {
    const confirmStartedAt = Date.now();
    setScanError('');
    setScanErrorItemId('');
    const paymentMethod = String(confirmOptions?.paymentMethod || '').trim().toLowerCase() === 'cash'
      ? 'cash'
      : 'card';
    const useStripeCheckout = paymentMethod === 'card' && Boolean(confirmOptions?.useStripeCheckout);
    const isTaxExempt = Boolean(confirmOptions?.isTaxExempt);
    const taxExemptBusinessName = String(confirmOptions?.taxExemptBusinessName || '').trim();
    const taxExemptTaxId = String(confirmOptions?.taxExemptTaxId || '').trim();
    if (isTaxExempt && (!taxExemptBusinessName || !taxExemptTaxId)) {
      setScanError('Tax exempt requires buyer business name and tax ID.');
      return false;
    }
    const subtotalAmountRaw = toNumberOrNull(confirmOptions?.subtotalAmount);
    const subtotalAmount = roundMoney(subtotalAmountRaw === null ? scanTotal : subtotalAmountRaw);
    const taxAmount = isTaxExempt
      ? 0
      : roundMoney(toNumberOrNull(confirmOptions?.taxAmount) || 0);
    const cardFeeAmount = paymentMethod === 'card'
      ? roundMoney(toNumberOrNull(confirmOptions?.cardFeeAmount) || 0)
      : 0;
    const totalAmountRaw = toNumberOrNull(confirmOptions?.totalAmount);
    const totalAmount = roundMoney(
      totalAmountRaw === null ? subtotalAmount + taxAmount + cardFeeAmount : totalAmountRaw
    );
    const salesTaxPctApplied = isTaxExempt ? 0 : normalizePercent(confirmOptions?.salesTaxPct);
    const cardFeePctApplied = paymentMethod === 'card' ? normalizePercent(confirmOptions?.cardFeePct) : 0;
    const discountPctApplied = normalizePercent(saleDiscPct);
    logScanDiag('confirm_scan_start', {
      scanItemsCount: scanItems.length,
      manualLinesCount: manualLines.length,
      manualOpen,
      selectedSaleId,
    });
    if (!scanItems.length && manualLines.every((line) => !String(line.title || '').trim() && !String(line.category || '').trim() && !line.price)) {
      setScanError('Add at least one item before confirming.');
      logScanDiag('confirm_scan_rejected_empty', {
        elapsedMs: Date.now() - confirmStartedAt,
      });
      return false;
    }
    if (hasMultipleSales) {
      setScanError('Scanned items belong to multiple sales. Scan items from one sale at a time.');
      logScanDiag('confirm_scan_rejected_multiple_sales', {
        elapsedMs: Date.now() - confirmStartedAt,
      });
      return false;
    }
    if (manualOpen) {
      const hasIncompleteManualLine = manualLines.some((line) => {
        const category = String(line?.category || '').trim();
        const price = toNumberOrNull(line?.price);
        return !category || price === null || price <= 0;
      });
      if (hasIncompleteManualLine) {
        setScanError(INCOMPLETE_MANUAL_LINE_ERROR);
        logScanDiag('confirm_scan_rejected_incomplete_manual_line', {
          elapsedMs: Date.now() - confirmStartedAt,
        });
        return false;
      }
    }
    for (const item of scanItems) {
      const soldPrice = toNumberOrNull(item.soldPrice);
      if (soldPrice === null || soldPrice <= 0) {
        setScanError('Each scanned item needs a valid sold price.');
        logScanDiag('confirm_scan_rejected_invalid_price', {
          itemId: item.id,
          elapsedMs: Date.now() - confirmStartedAt,
        });
        return false;
      }
      const qtySold = toIntOr(item.qtySold, null);
      if (!qtySold || qtySold < 1 || qtySold > item.qtyAvailable) {
        setScanError(`Maximum quantity of ${item.qtyAvailable}.`);
        setScanErrorItemId(item.id);
        logScanDiag('confirm_scan_rejected_invalid_qty', {
          itemId: item.id,
          qtySold,
          qtyAvailable: item.qtyAvailable,
          elapsedMs: Date.now() - confirmStartedAt,
        });
        return false;
      }
    }
    const scannedTransactionLines = scanItems.map((item) => {
      const quantity = toIntOr(item.qtySold, 1);
      const soldUnitPrice = toNumberOrNull(item.soldPrice) || 0;
      const askingUnitPrice = toNumberOrNull(item.listPrice);
      return {
        inventory_item_id: item.id,
        title: String(item.title || '').trim() || 'N/A',
        category: String(item.category || '').trim(),
        quantity,
        asking_unit_price: askingUnitPrice,
        sold_unit_price: soldUnitPrice,
        line_subtotal: Number((soldUnitPrice * quantity).toFixed(2)),
        is_manual: false,
      };
    });
    const manualTransactionLines = [];

    try {
      if (manualOpen) {
        const manualItems = buildManualItemsForConfirm();

        const missingCategory = manualItems.find((row) => !row.category);
        if (missingCategory) {
          setScanError('Each manual item requires a category.');
          return false;
        }

        const invalidManualPrice = manualItems.find(
          (row) => row.price === null || row.price <= 0
        );
        if (invalidManualPrice) {
          setScanError('Each manual item needs a valid price (numbers only).');
          logScanDiag('confirm_scan_rejected_invalid_manual_price', {
            title: invalidManualPrice.title,
            price: invalidManualPrice.price,
            elapsedMs: Date.now() - confirmStartedAt,
          });
          return false;
        }

        if (manualItems.length) {
          if (!selectedSaleId) {
            setScanError('Select a sale before adding manual items.');
            return false;
          }
          const activeRows = await fetchActiveCandidates(selectedSaleId);
          const { activeById, activeByTitle } = buildActiveItemIndexes(activeRows);

          const scannedItemIds = new Set(scanItems.map((item) => item.id));
          const reconcileResult = reconcileManualItemsForConfirm({
            manualItems,
            activeById,
            activeByTitle,
            scannedItemIds,
            selectedSaleIdForMatch: selectedSaleId,
          });
          if (reconcileResult.errorMessage) {
            setScanError(reconcileResult.errorMessage);
            return false;
          }
          const { matchedInventorySales, manualItemsToInsert, transactionLines } = reconcileResult;
          manualTransactionLines.push(...transactionLines);

          logScanDiag('confirm_scan_manual_match_summary', {
            matchedInventoryCount: matchedInventorySales.size,
            unmatchedManualCount: manualItemsToInsert.length,
          });

          await persistMatchedManualSales(matchedInventorySales);

          if (manualItemsToInsert.length) {
            if (typeof resolveManualSection !== 'function') {
              setScanError('Manual section resolver missing.');
              return false;
            }
            const { sectionId } = await resolveManualSection();
            const uploadLogResponse = await fetchWrapper(
              `${apiUrl}/supabase/rpc/get_or_create_manual_upload_log`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  p_client_id: clientId,
                  p_sale_id: selectedSaleId,
                  p_section_id: sectionId,
                }),
              }
            );
            if (!uploadLogResponse.ok) {
              const errorData = await uploadLogResponse.json().catch(() => ({}));
              throw new Error(errorData.error || 'Failed to prepare manual upload log');
            }
            const uploadLogId = await uploadLogResponse.json();
            const insertResponse = await fetchWrapper(
              `${apiUrl}/supabase/rpc/insert_manual_inventory_items`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  p_upload_log_id: uploadLogId,
                  p_client_id: clientId,
                  p_sale_id: selectedSaleId,
                  p_section_id: sectionId,
                  p_items: manualItemsToInsert,
                }),
              }
            );
            if (!insertResponse.ok) {
              const errorData = await insertResponse.json().catch(() => ({}));
              throw new Error(errorData.error || 'Failed to create manual items');
            }
            const insertedItems = await insertResponse.json();
            await persistInsertedManualItemSales(insertedItems, selectedSaleId);
          }
        }
      }
      const transactionSaleId = selectedSaleId || scanItems[0]?.saleId || null;
      if (!transactionSaleId) {
        setScanError('Select a sale before confirming checkout.');
        return false;
      }
      await persistScannedItemSales(scanItems, transactionSaleId);
      const allTransactionLines = [...scannedTransactionLines, ...manualTransactionLines];
      const checkoutSummary = {
        paymentMethod,
        useStripeCheckout,
        isTaxExempt,
        taxExemptBusinessName: isTaxExempt ? taxExemptBusinessName : null,
        taxExemptTaxId: isTaxExempt ? taxExemptTaxId : null,
        discountPctApplied,
        salesTaxPctApplied,
        cardFeePctApplied,
        subtotalAmount,
        taxAmount,
        cardFeeAmount,
        totalAmount,
        itemCount: allTransactionLines.reduce((sum, line) => sum + toIntOr(line.quantity, 0), 0),
      };
      const transactionMeta = await createSalesTransaction({
        saleId: transactionSaleId,
        checkoutSummary,
        itemLines: allTransactionLines,
      });
      await fetchInventory();
      logScanDiag('confirm_scan_success', {
        elapsedMs: Date.now() - confirmStartedAt,
      });
      return transactionMeta;
    } catch (err) {
      setScanError(err.message || 'Failed to record sale.');
      logScanDiag('confirm_scan_error', {
        error: safeErrorString(err?.message || err),
        elapsedMs: Date.now() - confirmStartedAt,
      });
      return false;
    }
  };

  const handleQrParam = useCallback((token, isAuthed) => {
    debugScanConsole('[SoldScan] handleQrParam', { token, isAuthed });
    if (!token) {
      lastQrRef.current = '';
      lastQrAuthedRef.current = false;
      return;
    }
    const authed = Boolean(isAuthed);
    const isRepeatToken = token === lastQrRef.current;
    if (isRepeatToken) {
      // Ignore repeated callbacks for the same token unless we are crossing from unauthenticated -> authenticated.
      if (lastQrAuthedRef.current === authed || lastQrAuthedRef.current) {
        logScanDiag('qr_param_ignored_repeat', {
          tokenSuffix: String(token || '').slice(-8),
          isAuthed: authed,
        });
        return;
      }
    }
    lastQrRef.current = token;
    lastQrAuthedRef.current = authed;
    if (!authed) {
      setScanError('Sign in to resolve the QR code.');
      setScanModalOpen(true);
      logScanDiag('qr_param_blocked_not_authed', {
        tokenSuffix: String(token || '').slice(-8),
      });
      return;
    }
    logScanDiag('qr_param_resolved', {
      tokenSuffix: String(token || '').slice(-8),
    });
    openScanModal();
    handleTokenScan(token);
  }, [handleTokenScan, openScanModal]);

  return {
    scanModalOpen,
    scanItems,
    manualLines,
    manualOpen,
    scanError,
    scanErrorItemId,
    showScanner,
    scanInProgress,
    scanTotal,
    hasMultipleSales,
    openScanModal,
    closeScanModal,
    startScan,
    stopScanner,
    stopScan,
    addManualLine,
    updateManualLine,
    removeManualLine,
    openManualItems,
    updateScanItem,
    removeScanItem,
    loadSelectedItemsIntoModal,
    handleConfirmScan,
    handleTokenScan,
    handleQrParam,
  };
}
